import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  InputAdornment,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Typography,
  Rating,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { applyFilters, clearFilters } from '../redux/slices/professionalsSlice';
import './ProfessionalsSearchBar.css';

const ProfessionalsSearchBar = ({ variant = 'full' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.professionals.filters);
  
  const [specialty, setSpecialty] = useState(filters.specialty || '');
  const [location, setLocation] = useState(filters.location || '');
  const [discipline, setDiscipline] = useState(filters.discipline || '');
  const [freelanceType, setFreelanceType] = useState(filters.freelanceType || '');
  const [priceRange, setPriceRange] = useState(filters.priceRange || [0, 200]);
  const [minRating, setMinRating] = useState(filters.minRating || 0);
  const [availability, setAvailability] = useState(filters.availability || '');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const disciplines = [
    'Coach sportif',
    'Kinésithérapeute',
    'Arbitre',
    'Préparateur physique',
    'Nutritionniste sportif',
    'Psychologue du sport',
    'Masseur sportif',
    'Entraîneur personnel',
    'Professeur de yoga',
    'Instructeur fitness',
    'Ostéopathe',
    'Podologue du sport',
  ];

  const locations = [
    'Paris',
    'Lyon',
    'Marseille',
    'Toulouse',
    'Nice',
    'Nantes',
    'Strasbourg',
    'Montpellier',
    'Bordeaux',
    'Lille',
    'Rennes',
    'Reims',
    'Grenoble',
    'Dijon',
    'Angers',
    'Villeurbanne',
    'Saint-Étienne',
    'Le Havre',
    'Toulon',
    'Clermont-Ferrand',
  ];

  const availabilityOptions = [
    { value: '', label: 'Toutes disponibilités' },
    { value: 'immediate', label: 'Disponible immédiatement' },
    { value: 'week', label: 'Disponible cette semaine' },
    { value: 'month', label: 'Disponible ce mois' },
    { value: 'flexible', label: 'Horaires flexibles' },
  ];

  const freelanceTypes = [
    { value: '', label: 'Tous les types' },
    { value: 'encadrement', label: 'Encadrement' },
    { value: 'sante-bien-etre', label: 'Santé & bien-être' },
    { value: 'evenementiel', label: 'Événementiel' },
    { value: 'marketing', label: 'Marketing' },
  ];

  const handleSearch = () => {
    // Aligne les clés de filtres avec le slice Redux et l'API
    const newFilters = {
      // Texte de recherche (utilisé par l'API pour nom/email/spécialités/discipline)
      search: specialty.trim(),
      // Localisation (ville)
      location: location.trim(),
      // Filtres avancés
      discipline,
      freelanceType,
      // Plage de prix -> rateMin/rateMax
      rateMin: Array.isArray(priceRange) ? priceRange[0] : 0,
      rateMax: Array.isArray(priceRange) ? priceRange[1] : 200,
      // Note minimale
      ratingMin: typeof minRating === 'number' ? minRating : 0,
      // Disponibilité (géré côté client uniquement)
      availability,
    };
    dispatch(applyFilters(newFilters));
  };

  const handleClearFilters = () => {
    setSpecialty('');
    setLocation('');
    setDiscipline('');
    setFreelanceType('');
    setPriceRange([0, 200]);
    setMinRating(0);
    setAvailability('');
    dispatch(clearFilters());
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const hasActiveFilters = specialty || location || discipline || freelanceType || priceRange[0] > 0 || priceRange[1] < 200 || minRating > 0 || availability;

  if (variant === 'compact') {
    return (
      <Paper
        className="professionals-search-bar professionals-search-bar--compact"
        elevation={2}
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderRadius: '25px',
          backgroundColor: 'background.paper',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          placeholder="Spécialité ou domaine"
          value={specialty}
          onChange={(e) => setSpecialty(e.target.value)}
          onKeyPress={handleKeyPress}
          size="small"
          sx={{
            flex: 1,
            minWidth: '200px',
            '& .MuiOutlinedInput-root': {
              border: 'none',
              '& fieldset': { border: 'none' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <WorkIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          placeholder="Localisation"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          onKeyPress={handleKeyPress}
          size="small"
          sx={{
            flex: 1,
            minWidth: '150px',
            '& .MuiOutlinedInput-root': {
              border: 'none',
              '& fieldset': { border: 'none' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          sx={{
            borderRadius: '20px',
            minWidth: 'auto',
            px: 2,
          }}
        >
          <SearchIcon />
        </Button>
        {hasActiveFilters && (
          <IconButton
            onClick={handleClearFilters}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <ClearIcon />
          </IconButton>
        )}
      </Paper>
    );
  }

  return (
    <Box className="professionals-search-bar professionals-search-bar--full">
      <Paper
        elevation={3}
        sx={{
          p: isMobile ? 2 : 3,
          borderRadius: '20px',
          backgroundColor: 'background.paper',
          maxWidth: '1000px',
          margin: '0 auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
      >
        {/* Main Search Bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0,
            alignItems: 'stretch',
            border: '2px solid #e0e0e0',
            borderRadius: '25px',
            overflow: 'hidden',
            backgroundColor: 'white',
            mb: 2,
          }}
        >
          {/* Specialty Field */}
          <TextField
            placeholder="Spécialité ou domaine"
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={{
              flex: isMobile ? 1 : 1.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: isMobile ? '20px' : '0',
                border: 'none',
                backgroundColor: 'transparent',
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' },
                minHeight: '56px',
              },
              '& .MuiInputBase-input': {
                fontSize: '1rem',
                fontWeight: 500,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <WorkIcon sx={{ color: '#666', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Divider */}
          {!isMobile && (
            <Box
              sx={{
                width: '1px',
                backgroundColor: '#e0e0e0',
                alignSelf: 'stretch',
                my: 1,
              }}
            />
          )}

          {/* Location Field */}
          <Autocomplete
            freeSolo
            options={locations}
            value={location}
            onInputChange={(event, newValue) => {
              setLocation(newValue || '');
            }}
            sx={{
              flex: isMobile ? 1 : 1.2,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Localisation (ville, région...)"
                onKeyPress={handleKeyPress}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: isMobile ? '20px' : '0',
                    border: 'none',
                    backgroundColor: 'transparent',
                    '& fieldset': { border: 'none' },
                    '&:hover fieldset': { border: 'none' },
                    '&.Mui-focused fieldset': { border: 'none' },
                    minHeight: '56px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '1rem',
                    fontWeight: 500,
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon sx={{ color: '#666', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* Search Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            size="large"
            sx={{
              borderRadius: isMobile ? '20px' : '0 20px 20px 0',
              px: isMobile ? 3 : 4,
              py: isMobile ? 1.5 : 'auto',
              minHeight: '56px',
              minWidth: isMobile ? 'auto' : '140px',
              fontWeight: 600,
              fontSize: '1rem',
              textTransform: 'none',
              boxShadow: 'none',
              background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
              '&:hover': {
                boxShadow: '0 4px 15px rgba(25, 118, 210, 0.4)',
                background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.3s ease',
            }}
            startIcon={<SearchIcon />}
          >
            Rechercher
          </Button>
        </Box>

        {/* Advanced Filters Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Button
            startIcon={<FilterIcon />}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: showAdvancedFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                }}
              />
            }
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Filtres avancés
          </Button>
          {hasActiveFilters && (
            <Button
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              sx={{
                color: 'error.main',
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Effacer les filtres
            </Button>
          )}
        </Box>

        {/* Advanced Filters */}
        <Collapse in={showAdvancedFilters}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 3,
              p: 3,
              backgroundColor: '#f8f9fa',
              borderRadius: '15px',
              border: '1px solid #e0e0e0',
            }}
          >
            {/* Discipline Filter */}
            <FormControl fullWidth>
              <InputLabel>Discipline</InputLabel>
              <Select
                value={discipline}
                onChange={(e) => setDiscipline(e.target.value)}
                label="Discipline"
                sx={{ borderRadius: '10px' }}
              >
                <MenuItem value="">
                  <em>Toutes les disciplines</em>
                </MenuItem>
                {disciplines.map((disc) => (
                  <MenuItem key={disc} value={disc}>
                    {disc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Freelance Type Filter */}
            <FormControl fullWidth>
              <InputLabel>Type de freelance</InputLabel>
              <Select
                value={freelanceType}
                onChange={(e) => setFreelanceType(e.target.value)}
                label="Type de freelance"
                sx={{ borderRadius: '10px' }}
              >
                {freelanceTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Price Range Filter */}
            <Box>
              <Typography gutterBottom sx={{ fontWeight: 500, color: 'text.secondary' }}>
                Tarif horaire (€)
              </Typography>
              <Slider
                value={priceRange}
                onChange={(event, newValue) => setPriceRange(newValue)}
                valueLabelDisplay="auto"
                min={0}
                max={200}
                step={5}
                marks={[
                  { value: 0, label: '0€' },
                  { value: 50, label: '50€' },
                  { value: 100, label: '100€' },
                  { value: 150, label: '150€' },
                  { value: 200, label: '200€+' },
                ]}
                sx={{
                  color: 'primary.main',
                  '& .MuiSlider-thumb': {
                    backgroundColor: 'primary.main',
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: 'primary.main',
                  },
                }}
              />
            </Box>

            {/* Rating Filter */}
            <Box>
              <Typography gutterBottom sx={{ fontWeight: 500, color: 'text.secondary' }}>
                Note minimum
              </Typography>
              <Rating
                value={minRating}
                onChange={(event, newValue) => setMinRating(newValue || 0)}
                precision={0.5}
                size="large"
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: '#ffc107',
                  },
                  '& .MuiRating-iconEmpty': {
                    color: '#e0e0e0',
                  },
                }}
              />
            </Box>

            {/* Availability Filter */}
            <FormControl fullWidth>
              <InputLabel>Disponibilité</InputLabel>
              <Select
                value={availability}
                onChange={(e) => setAvailability(e.target.value)}
                label="Disponibilité"
                sx={{ borderRadius: '10px' }}
              >
                {availabilityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Collapse>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {specialty && (
              <Chip
                label={`Spécialité: ${specialty}`}
                onDelete={() => setSpecialty('')}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {location && (
              <Chip
                label={`Lieu: ${location}`}
                onDelete={() => setLocation('')}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {discipline && (
              <Chip
                label={`Discipline: ${discipline}`}
                onDelete={() => setDiscipline('')}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {freelanceType && (
              <Chip
                label={`Type: ${freelanceTypes.find(type => type.value === freelanceType)?.label}`}
                onDelete={() => setFreelanceType('')}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {(priceRange[0] > 0 || priceRange[1] < 200) && (
              <Chip
                label={`Prix: ${priceRange[0]}€ - ${priceRange[1]}€`}
                onDelete={() => setPriceRange([0, 200])}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {minRating > 0 && (
              <Chip
                label={`Note: ${minRating}+ ⭐`}
                onDelete={() => setMinRating(0)}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {availability && (
              <Chip
                label={`Disponibilité: ${availabilityOptions.find(opt => opt.value === availability)?.label}`}
                onDelete={() => setAvailability('')}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ProfessionalsSearchBar;